// Sorting bank transactions into categories, in plain words and keyboard-first (BRIEF.md section 9).
// AI suggestions arrive pre-filled; Enter confirms. "Always" saves a rule for the merchant.
import { merchantKey } from '@foderis/domain';
import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOrgData } from '../accountant/useOrgData.js';
import type { Api } from '../api.js';
import { EmptyState } from '../EmptyState.js';
import { day, dollars, fromCents } from '../format.js';
import { useNarrow } from '../useNarrow.js';

interface Suggestion {
  categoryId: string;
  taxCode: string;
  productId?: string;
  confidence: number;
  reason: string;
}
interface Line {
  id: string;
  date: string;
  amount_minor: string;
  description: string;
  status: string;
  evidence: { foreignCountry?: string; foreignAmount?: string } | null;
  suggestion: Suggestion | null;
}
interface Category {
  id: string;
  name: string;
  direction: 'in' | 'out';
  default_tax_code: string | null;
  account_type: string;
}
interface Dimension {
  position: number;
  name: string;
  values: { id: string; name: string }[];
}

const GST_CHOICES = {
  out: [
    ['AU:INP', 'Includes GST'],
    ['AU:NGP', 'No GST'],
    ['AU:BX', 'Not on the BAS'],
  ],
  in: [
    ['AU:GST', 'Includes GST'],
    ['AU:FRE', 'GST-free'],
    ['AU:EXP', 'Export, GST-free'],
    ['AU:BX', 'Not on the BAS'],
  ],
} as const;

export function SortQueue(props: {
  api: Api;
  orgId: string;
  bankAccountId: string;
  onChanged: () => void;
}) {
  const { api, orgId, bankAccountId } = props;
  const lines = useOrgData<{ lines: Line[] }>(api, orgId, `/bank-accounts/${bankAccountId}/lines`);
  const categories = useOrgData<{ categories: Category[] }>(api, orgId, '/categories');
  const tracking = useOrgData<{ dimensions: Dimension[] }>(api, orgId, '/tracking');
  const [error, setError] = useState('');
  const [thinking, setThinking] = useState(false);
  const [current, setCurrent] = useState(0);
  // On a phone, one transaction at a time; on a computer, the whole list for the keyboard.
  const narrow = useNarrow();
  const asked = useRef(false);
  const reloadLines = lines.reload;
  const onChanged = props.onChanged;

  const refresh = useCallback(() => {
    void reloadLines();
    onChanged();
  }, [reloadLines, onChanged]);

  const todo = (lines.data?.lines ?? []).filter((l) => l.status === 'unreconciled');
  const hasCategories = (categories.data?.categories.length ?? 0) > 0;
  const needsSuggestions = todo.some((l) => !l.suggestion);

  // Ask the AI once per visit for lines without a suggestion. Unavailable locally: silently skipped.
  useEffect(() => {
    if (!hasCategories || !needsSuggestions || asked.current) return;
    asked.current = true;
    setThinking(true);
    // Batches of up to 15 lines per call; keep asking while each call still finds lines to suggest for.
    (async () => {
      for (let batch = 0; batch < 10; batch++) {
        const { suggested } = await api.send<{ suggested: number }>(
          orgId,
          'POST',
          `/bank-accounts/${bankAccountId}/suggest`,
        );
        await reloadLines();
        if (suggested === 0) break;
      }
    })()
      .catch(() => undefined)
      .finally(() => setThinking(false));
  }, [api, orgId, bankAccountId, hasCategories, needsSuggestions, reloadLines]);

  async function setUp() {
    setError('');
    try {
      await api.send(orgId, 'POST', '/categories/setup');
      await categories.reload();
      await api.send(orgId, 'POST', `/bank-accounts/${bankAccountId}/apply-rules`);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set up categories.');
    }
  }

  async function unsort(id: string) {
    setError('');
    try {
      await api.send(orgId, 'POST', `/bank-lines/${id}/unsort`);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not undo that.');
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const tag = (event.target as HTMLElement).tagName;
    if (
      tag === 'SELECT' ||
      (tag === 'INPUT' && (event.target as HTMLInputElement).type !== 'checkbox')
    )
      return;
    if (event.key === 'ArrowDown' || event.key === 'j') {
      event.preventDefault();
      setCurrent((i) => Math.min(i + 1, todo.length - 1));
    } else if (event.key === 'ArrowUp' || event.key === 'k') {
      event.preventDefault();
      setCurrent((i) => Math.max(i - 1, 0));
    }
  }

  if (!lines.data || !categories.data || !tracking.data) return <p className="muted">Loading</p>;
  if (!hasCategories) {
    return (
      <div className="stack">
        <p>
          Before sorting, Foderis needs its list of categories (Software, Bank fees, Stripe payout
          and so on).
        </p>
        <button type="button" onClick={setUp}>
          Set up categories
        </button>
        {error && <p className="error">{error}</p>}
      </div>
    );
  }
  const done = lines.data.lines.filter((l) => l.status === 'reconciled');
  const product = tracking.data.dimensions.find((d) => d.position === 1);
  const focus = Math.min(current, Math.max(todo.length - 1, 0));
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keyboard shortcuts for the whole queue
    <div className="stack" onKeyDown={onKeyDown}>
      {todo.length + done.length > 0 && (
        <div className="progress-row">
          <progress
            value={done.length}
            max={todo.length + done.length}
            aria-label="Sorted so far"
          />
          <span className="muted">
            {done.length} of {todo.length + done.length} sorted
          </span>
        </div>
      )}
      {todo.length > 0 && !narrow && (
        <p className="muted">
          Enter confirms. Up and down (or j and k) move between transactions. c chooses the
          category, a ticks "always".
          {thinking && ' Getting suggestions.'}
        </p>
      )}
      {todo.length === 0 ? (
        <EmptyState art="all-sorted">
          <h2>Everything is sorted</h2>
        </EmptyState>
      ) : (
        (narrow ? todo.slice(focus, focus + 1) : todo).map((line, j) => {
          const i = narrow ? focus : j;
          return (
            <SortLine
              key={line.id}
              api={api}
              orgId={orgId}
              line={line}
              active={i === focus}
              onFocus={() => setCurrent(i)}
              categories={categories.data?.categories ?? []}
              product={product}
              onSorted={refresh}
            />
          );
        })
      )}
      {narrow && todo.length > 1 && (
        <button
          type="button"
          className="secondary"
          onClick={() => setCurrent((focus + 1) % todo.length)}
        >
          Skip for now
        </button>
      )}
      {done.length > 0 && (
        <details>
          <summary>{done.length} sorted</summary>
          <table className="table">
            <tbody>
              {done.map((l) => (
                <tr key={l.id}>
                  <td>{day(l.date)}</td>
                  <td>{l.description}</td>
                  <td className="num">{dollars(fromCents(l.amount_minor))}</td>
                  <td>
                    <button type="button" className="link" onClick={() => unsort(l.id)}>
                      Undo
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function SortLine(props: {
  api: Api;
  orgId: string;
  line: Line;
  active: boolean;
  onFocus: () => void;
  categories: Category[];
  product: Dimension | undefined;
  onSorted: () => void;
}) {
  const { line, product, active } = props;
  const direction = line.amount_minor.startsWith('-') ? 'out' : 'in';
  const options = props.categories.filter((c) => c.direction === direction);
  const s = line.suggestion;
  const [categoryId, setCategoryId] = useState(s?.categoryId ?? '');
  const [taxCode, setTaxCode] = useState(s?.taxCode ?? '');
  const [tracking1, setTracking1] = useState(s?.productId ?? '');
  const [always, setAlways] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const categoryRef = useRef<HTMLSelectElement>(null);
  const category = options.find((c) => c.id === categoryId);
  const key = useMemo(() => merchantKey(line.description), [line.description]);
  const needsProduct =
    !!product && (category?.account_type === 'income' || category?.account_type === 'expense');

  // A suggestion that arrives after the card rendered fills the empty fields.
  useEffect(() => {
    if (!s) return;
    setCategoryId((v) => v || s.categoryId);
    setTaxCode((v) => v || s.taxCode);
    setTracking1((v) => v || s.productId || '');
  }, [s]);

  useEffect(() => {
    if (active) confirmRef.current?.focus({ preventScroll: false });
  }, [active]);

  function chooseCategory(id: string) {
    setCategoryId(id);
    setTaxCode(options.find((c) => c.id === id)?.default_tax_code ?? '');
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!categoryId || busy) return;
    setBusy(true);
    setError('');
    try {
      await props.api.send(props.orgId, 'POST', `/bank-lines/${line.id}/sort`, {
        categoryId,
        ...(taxCode ? { taxCode } : {}),
        ...(needsProduct && tracking1 ? { tracking1 } : {}),
        ...(always && key ? { alwaysContains: key } : {}),
      });
      props.onSorted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not sort that.');
      setBusy(false);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLFormElement>) {
    if ((event.target as HTMLElement).tagName === 'SELECT') return;
    if (event.key === 'c') {
      event.preventDefault();
      categoryRef.current?.focus();
    } else if (event.key === 'a' && key) {
      event.preventDefault();
      setAlways((v) => !v);
    }
  }

  const amount = fromCents(line.amount_minor);
  return (
    <form
      className={`sort-line ${active ? 'current' : ''}`}
      onSubmit={submit}
      onFocus={props.onFocus}
      onKeyDown={onKeyDown}
    >
      <div className="title-row">
        <span>
          <strong>{line.description}</strong>
          <span className="muted">
            {' '}
            {day(line.date)}
            {line.evidence?.foreignAmount &&
              ` (${Number(line.evidence.foreignAmount)} from ${line.evidence.foreignCountry})`}
          </span>
        </span>
        <span className={direction === 'in' ? 'money-in' : 'money-out'}>{dollars(amount)}</span>
      </div>
      {s && (
        <p className="muted suggestion">
          Suggested{s.confidence < 0.6 ? ', not sure' : ''}: {s.reason}
        </p>
      )}
      <div className="sort-fields">
        <select
          ref={categoryRef}
          value={categoryId}
          onChange={(e) => chooseCategory(e.target.value)}
          required
          aria-label="Category"
        >
          <option value="">
            {direction === 'in' ? 'What is this money?' : 'What was this for?'}
          </option>
          {options.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {category && (
          <select value={taxCode} onChange={(e) => setTaxCode(e.target.value)} aria-label="GST">
            {GST_CHOICES[direction].map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        )}
        {needsProduct && product && (
          <select
            value={tracking1}
            onChange={(e) => setTracking1(e.target.value)}
            required
            aria-label={product.name}
          >
            <option value="">{product.name}</option>
            {product.values.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        )}
        <button ref={confirmRef} type="submit" disabled={busy || !categoryId}>
          Confirm
        </button>
      </div>
      {key && (
        <label className="inline muted">
          <input type="checkbox" checked={always} onChange={(e) => setAlways(e.target.checked)} />
          Always do this for "{key}"
        </label>
      )}
      {error && <p className="error">{error}</p>}
    </form>
  );
}
